import type {
  SsoOrganizationPickerOrganizationTree,
  SsoOrganizationPickerOrganizationUnit,
  SsoOrganizationPickerTreeNode,
} from "./sso-organization-user-picker-types";

export function formatCustomFieldCellValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function buildOrganizationTreeKey(organizationId: number) {
  return `org:${organizationId}`;
}

export function buildOrganizationUnitTreeKey(organizationId: number, unitId: number) {
  return `unit:${organizationId}:${unitId}`;
}

export function parsePickerTreeKey(
  key: string | null | undefined,
): { organizationId: number; unitId: number | null } | null {
  const normalized = String(key || "").trim();
  if (!normalized) {
    return null;
  }
  const organizationMatch = normalized.match(/^org:(\d+)$/);
  if (organizationMatch) {
    return { organizationId: Number(organizationMatch[1]), unitId: null };
  }
  const unitMatch = normalized.match(/^unit:(\d+):(\d+)$/);
  if (unitMatch) {
    return { organizationId: Number(unitMatch[1]), unitId: Number(unitMatch[2]) };
  }
  return null;
}

export function normalizeExistingUserId(value: string | number): number | null {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }
  return Math.trunc(numericValue);
}

export function flattenOrganizationUnits(
  rootUnit: SsoOrganizationPickerOrganizationUnit,
): SsoOrganizationPickerOrganizationUnit[] {
  return [rootUnit, ...rootUnit.children.flatMap((child) => flattenOrganizationUnits(child))];
}

export function filterOrganizationUnitTree(
  unit: SsoOrganizationPickerOrganizationUnit,
  keyword: string,
): SsoOrganizationPickerOrganizationUnit | null {
  if (!keyword) {
    return unit;
  }
  const filteredChildren = unit.children
    .map((child) => filterOrganizationUnitTree(child, keyword))
    .filter((child): child is SsoOrganizationPickerOrganizationUnit => child !== null);
  const searchText = unit.name.toLowerCase();
  if (searchText.includes(keyword) || filteredChildren.length > 0) {
    return { ...unit, children: filteredChildren };
  }
  return null;
}

export function buildUnitTreeNodes(
  organizationId: number,
  units: SsoOrganizationPickerOrganizationUnit[],
): SsoOrganizationPickerTreeNode[] {
  return units.map((unit) => ({
    key: buildOrganizationUnitTreeKey(organizationId, unit.id),
    title: unit.name,
    children: buildUnitTreeNodes(organizationId, unit.children),
  }));
}

export function buildOrganizationTreeNodes(
  organizationTrees: SsoOrganizationPickerOrganizationTree[],
  keyword: string,
): SsoOrganizationPickerTreeNode[] {
  const normalizedKeyword = keyword.trim().toLowerCase();
  return organizationTrees.flatMap((tree) => {
    const organizationMatch = tree.organization.name.toLowerCase().includes(normalizedKeyword);
    const rootUnitMatch = tree.root_unit.name.toLowerCase().includes(normalizedKeyword);
    const filteredChildren = normalizedKeyword
      ? tree.root_unit.children
          .map((child) => filterOrganizationUnitTree(child, normalizedKeyword))
          .filter((child): child is SsoOrganizationPickerOrganizationUnit => child !== null)
      : tree.root_unit.children;
    if (
      normalizedKeyword &&
      !organizationMatch &&
      !rootUnitMatch &&
      filteredChildren.length === 0
    ) {
      return [];
    }
    return [
      {
        key: buildOrganizationTreeKey(tree.organization.id),
        title: tree.organization.name,
        children: buildUnitTreeNodes(
          tree.organization.id,
          organizationMatch || rootUnitMatch ? tree.root_unit.children : filteredChildren,
        ),
      },
    ];
  });
}
